import { unLinkWorkOrderDrawings } from "@/pages/work-orders/repositorie/WorkOrdersRepositorie";
import { useWorkOrderModalsContext } from "@/shared/work-orders-modals/context/work-order-modals.context";
import type { GridApi } from "ag-grid-community";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

export const useWorkOrderDrawingsActionsAndButtons = ({
	gridRef,
	searchWorkOrderDrawings,
	setRemoveDrawingError,
	WorkOrderId,
}: {
	WorkOrderId?: number | null;
	newMode?: boolean;
	gridRef: GridApi | null;
	searchWorkOrderDrawings: Function;
	setRemoveDrawingError: (err: string) => void | null;
	setShowLinkResourcesToAssetDialog?: Function;
	setListMessage: (err: string) => void | null;
}) => {
	const {
		state: { rowSelectedInDrawing, workOrderDrawingsList },
		actions: {
			setFirstTime,
			setRowSelectedInDrawing,
			setDrawingSearch,
			setDataDrawingSelected,
			setWorkOrderDrawingsList,
			setListDrawingsSelected,
		},
	} = useWorkOrderModalsContext();

	const [searchParams] = useSearchParams();
	const [refreshTrigger, setRefreshTrigger] = useState(1);
	const isMountRef = useRef(true);

	// BUTTONS STATES
	const [addButton, setAddButton] = useState<boolean>(true);
	const [deleteButton, setDeleteButton] = useState<boolean>(false);
	const [saveButton, setSaveButton] = useState<boolean>(false);
	const [discardButton, setDiscardButton] = useState<boolean>(false);
	const [deleteOpen, setDeleteOpen] = useState(false);

	const refresh = useCallback(() => {
		if (!isMountRef.current) {
			const updatedList = workOrderDrawingsList.filter(
				(item: any) => item.id !== -1,
			);

			setWorkOrderDrawingsList(updatedList);
			setFirstTime(true);
			setSaveButton(false);
			setDiscardButton(false);
			setAddButton(true);
			setDeleteButton(false);
			setDeleteOpen(false);
			setDrawingSearch("");
			setDataDrawingSelected(undefined);
			searchWorkOrderDrawings();
		}
	}, [searchParams, WorkOrderId]);

	const cancelAddDrawing = useCallback(() => {
		const updatedList = workOrderDrawingsList.filter(
			(item: any) => item.id !== -1,
		);

		setWorkOrderDrawingsList(updatedList);
		setDataDrawingSelected(null);
		setAddButton(true);
		setSaveButton(false);
		setDiscardButton(false);
		setDeleteButton(false);
		setDrawingSearch("");
	}, [
		workOrderDrawingsList,
		setWorkOrderDrawingsList,
		setDataDrawingSelected,
		setAddButton,
		setSaveButton,
		setDiscardButton,
		setDeleteButton,
		setDrawingSearch,
	]);

	const addRow = useCallback(() => {
		setAddButton(false);
		setSaveButton(true);
		setDiscardButton(true);
		setDeleteButton(false);
		const newData = { id: -1, WorkOrderId: WorkOrderId };
		const currentData = workOrderDrawingsList ? workOrderDrawingsList : [];
		const data = [newData, ...currentData];
		setWorkOrderDrawingsList(data);
	}, [workOrderDrawingsList, WorkOrderId]);

	const removeDrawings = useCallback(
		async (drawingsToRemove?: any[]) => {
			if (drawingsToRemove && drawingsToRemove.length > 0) {
				const formattedDrawings = drawingsToRemove.map((drawing: any) => ({
					DocumentName: drawing.DocumentName,
					IsAssetResource: drawing.IsAssetResource,
				}));

				const res = await unLinkWorkOrderDrawings(
					WorkOrderId as number,
					formattedDrawings,
				);
				if (res.ok) {
					refresh();
				} else {
					setRemoveDrawingError(res.message);
				}
			} else if (rowSelectedInDrawing) {
				const formattedDrawings = [
					{
						DocumentName: rowSelectedInDrawing.DocumentName,
						IsAssetResource: rowSelectedInDrawing.IsAssetResource,
					},
				];

				const res = await unLinkWorkOrderDrawings(
					WorkOrderId as number,
					formattedDrawings,
				);
				if (res.ok) {
					refresh();
				} else {
					setRemoveDrawingError(res.message);
				}
			}

			setRowSelectedInDrawing(null);
			setDeleteOpen(false);
			setAddButton(true);
			setSaveButton(false);
			setDiscardButton(false);
			setDeleteButton(false);
			setDrawingSearch("");
			setListDrawingsSelected([]);

			if (gridRef) {
				gridRef.deselectAll();
			}
		},
		[
			WorkOrderId,
			unLinkWorkOrderDrawings,
			refresh,
			setRemoveDrawingError,
			rowSelectedInDrawing,
			setRowSelectedInDrawing,
			setDeleteOpen,
			setAddButton,
			setSaveButton,
			setDiscardButton,
			setDeleteButton,
			setDrawingSearch,
			setListDrawingsSelected,
			gridRef,
		],
	);

	useEffect(() => {
		refresh();
	}, [refreshTrigger]);

	useEffect(() => {
		isMountRef.current = false;
	}, []);

	return {
		refresh,
		cancelAddDrawing,
		removeDrawings,
		addRow,
		addButton,
		deleteButton,
		saveButton,
		discardButton,
		deleteOpen,
		setSaveButton,
		setDiscardButton,
		setAddButton,
		setDeleteButton,
		setDeleteOpen,
	};
};
